import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import { SeoService } from '../services/seo.service';
import { LoggerService } from '../logger/logger.service';
import { AnalyticsService } from '../analytics/analytics.service';
import { MineSnackbarService } from '../shared/mine-snackbar/mine-snackbar.service';
import { RouterEventsListenerService } from '../services/router-events-listener.service';
import { ContentPipe } from '../services/content/content.pipe';
import { CollaborationNavigationService } from './services/collaboration-navigation.service';
import { COLLAB_COMPANY_ID_KEY } from './services/collaboration-token-store.service';

@Component({
    standalone: false,
    selector: 'app-root',
    templateUrl: './collaboration.component.html',
    styleUrls: ['./collaboration.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CollaborationComponent implements OnInit {
    private readonly loggerName: string = 'CollaborationComponent';

    snackbarOpen: boolean = false;
    snackbarState: 'open' | 'closed';

    constructor(
        private routerEventListener: RouterEventsListenerService,
        private snackbarService: MineSnackbarService,
        private analytics: AnalyticsService,
        private logger: LoggerService,
        private cdr: ChangeDetectorRef,
        private destroyRef: DestroyRef,
        private seoService: SeoService,
        private contentPipe: ContentPipe,
        private navigationService: CollaborationNavigationService,
    ) {}

    ngOnInit() {
        this.logger.debug(this.loggerName, 'ngOnInit()');
        this.seoService.init();
        this.snackbarState = 'closed';
        this.manageSnackbarDisplay();
        this.listenForCompanySwitch();

        this.routerEventListener.url$.pipe(
            takeUntilDestroyed(this.destroyRef))
            .subscribe((response) => this.handleRoute(response));
    }

    private handleRoute(route: string): void {
        if (route !== '') {
            this.analytics.pageView(route);
        }
    }

    private manageSnackbarDisplay(): void {
        this.snackbarService.visible$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(visible => {
            if (visible) {
                this.snackbarOpen = true;
                this.snackbarState = 'open';
                this.cdr.markForCheck();
            }
            else {
                this.snackbarOpen = false;
                this.snackbarState = 'closed';
                this.cdr.markForCheck();
            }
        });
    }

    /**
     * Listens for localStorage changes from other tabs.
     * When another tab switches to a different company, navigates to the session-ended error page.
     * Fires only when companyId changes to a new non-null value that differs from the previous one,
     * ignoring initial writes (oldValue=null) and removals (newValue=null).
     */
    private listenForCompanySwitch(): void {
        fromEvent<StorageEvent>(window, 'storage').pipe(
            filter(event =>
                event.key === COLLAB_COMPANY_ID_KEY &&
                !!event.oldValue &&
                !!event.newValue &&
                event.oldValue !== event.newValue
            ),
            tap(() => this.logger.debug(this.loggerName, 'Company switch detected in another tab, ending session')),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe(() => {
            const message = this.contentPipe.transform('ai-assessments-collaboration.sessionEnded');
            this.navigationService.navigateToAuthenticationErrorPage(message);
        });
    }
}
